import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { Alert } from '../CommonComponents/OPL/Alert/Alert';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Tab, TabList, Tabs } from '../CommonComponents/OPL/Tab';
import { Text } from '../CommonComponents/OPL/Text/Text';
import Spinner from '../CommonComponents/Spinner';
import {
  isOrionClientSelector,
  selectCanExportPersonalFinancesToCsv,
  selectCurrentClient,
  selectHasFirmSettingsAccess,
  selectShowIncomeTab,
  showOrionPerformanceSelector,
  showTransactionsSelector,
} from '../CommonSelectors/CommonAreaSelector';
import {
  accountGroupsSelector,
  accountsSelector,
  selectAreManagedAccountGroupsSelectable,
  selectManagedAccountGroups,
  selectUnmanagedAccountsLabel,
} from '../ContextualSidebar/NetWorthSidebar/NetWorthSidebarSelector';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';
import { useIsEmbeddedMobile } from '../_hooks/useIsEmbeddedMobile';
import { unmanagedGroupId } from './AccountPerformanceReporting.constants';
import { Header, RightMenu, TabsWrapper } from './AccountPerformanceReporting.styles';
import { AccountHeader } from './components/AccountHeader/AccountHeader';
import { AccountMenu } from './components/AccountMenu/AccountMenu';
import { PeriodSelector } from './components/PeriodSelector/PeriodSelector';
import { RefreshButton } from './components/RefreshButton/RefreshButton';
import { RmdDashboard } from './components/RmdDashboard/RmdDashboard';
import { useOptions } from './hooks/useOptions';
import { useRouteParams } from './hooks/useRouteParams';
import { useUpdatedAt } from './hooks/useUpdatedAt';
import { fetchWidgetsOptions, setFocusedAccountId } from './state/actions';
import { areOptionsLoadedSelector, focusedAccountIdSelector } from './state/selectors';
import { Performance } from './tabs/Performance/Performance';
import { DateSelector } from './tabs/Positions/components/DateSelector/DateSelector';
import { Positions } from './tabs/Positions/Positions';
import { RiskProfile } from './tabs/RiskProfile/RiskProfile';
import { Summary } from './tabs/Summary/Summary';
import Transactions from './tabs/Transactions/Transactions';
import { ManagedAccountGroups } from './types';
import { PerformanceReportingProvider } from './context/PerformanceReportingProvider';
import { ADD_CARD_BUTTON_CONTAINER_ID } from './components/DesktopWidgets/DesktopWidgets.meta';
import { Income } from './tabs/Income/Income';
import { useTabLabels } from './hooks/useTabLabels';
import { EXPORT_CSV_BUTTON_CONTAINER_ID } from './hooks/useExportCsvButtonContainer';

let timer;

interface Props {
  isLandingPageOption: string;
}

export const AccountPerformanceReporting = ({ isLandingPageOption }: Props) => {
  const [canShowComponent, setCanShowComponent] = useState(false);

  const [showAccountUpdateAlert, setShowAccountUpdateAlert] = useState(false);

  const { clientId, tabSlug, accountId, groupId, isApex: isApexRouteParam } = useRouteParams();

  const areOptionsLoaded = useSelector(areOptionsLoadedSelector);

  const isOrionClient = useSelector(isOrionClientSelector);

  const accounts = useSelector(accountsSelector);

  const focusedAccountId = useSelector(focusedAccountIdSelector);

  const accountGroups = useSelector(accountGroupsSelector);

  const managedAccountGroups = useSelector(selectManagedAccountGroups) as ManagedAccountGroups;

  const currentClient = useSelector(selectCurrentClient);

  const showOrionPerformance = useSelector(showOrionPerformanceSelector);

  const showActivity = useSelector(showTransactionsSelector);

  const showIncomeTab = useSelector(selectShowIncomeTab);

  const areManagedAccountGroupsSelectable = useSelector(selectAreManagedAccountGroupsSelectable);

  const isEmbeddedMobile = useIsEmbeddedMobile();

  const isFirmLevelUser = useSelector(selectHasFirmSettingsAccess);

  const unmanagedAccountsLabel = useSelector(selectUnmanagedAccountsLabel);

  const { showGrossOfFeeDisclaimer } = useOptions();

  const tabLabels = useTabLabels();

  const canExportPersonalFinancesToCsv = useSelector(selectCanExportPersonalFinancesToCsv);

  const dispatch = useDispatch();

  useEffectOnce(() => {
    dispatch(setFocusedAccountId(groupId || accountId));

    if (!areOptionsLoaded) {
      dispatch(fetchWidgetsOptions(clientId));
    }

    timer = setTimeout(() => setCanShowComponent(true), 10);

    return () => clearTimeout(timer);
  });

  useEffect(() => {
    if (!isOrionClient && !accountId && !groupId && !isLandingPageOption) {
      ADV.util.Router.go2('Transactions', { clientId, accountId: '0' });
    }
  }, [accountId, clientId, groupId, isOrionClient, isLandingPageOption]);

  const isApex =
    isApexRouteParam && isApexRouteParam !== 'undefined' ? JSON.parse(isApexRouteParam) : false;

  const managedAccountId =
    isApex && !isEmpty(accounts) ? accounts[accountId].ManagedAccountId : null;

  useDocumentTitle('Personal Finances');

  const periodComponent = useMemo(() => {
    switch (tabSlug) {
      case 'summary':
      case 'performance':
      case 'income':
        return (
          <>
            {isFirmLevelUser && <Box id={ADD_CARD_BUTTON_CONTAINER_ID} />}

            <PeriodSelector />
          </>
        );
      case 'allocation':
        return (
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Text variant="formLabel" mr="8" css="white-space: nowrap">
              As of
            </Text>

            <DateSelector isEmbeddedMobile={isEmbeddedMobile} />
          </Box>
        );
      case 'activity':
        return <PeriodSelector />;
      default:
        return null;
    }
  }, [isEmbeddedMobile, isFirmLevelUser, tabSlug]);

  const selectedItemName = useMemo(() => {
    if (groupId === unmanagedGroupId) {
      return unmanagedAccountsLabel;
    }

    if (!groupId && !accountId) {
      return 'Managed Accounts';
    }

    if (areManagedAccountGroupsSelectable) {
      const selectedGroup = Object.values(managedAccountGroups).find(
        ({ portfolioGroup }) => portfolioGroup.id === groupId,
      );

      if (selectedGroup) {
        return selectedGroup.portfolioGroup.name || selectedGroup.portfolioGroup.nameId;
      }
    }

    const foundAccount = accountGroups
      .flatMap(group => group.Items.flatMap(item => item.Item))
      .find(item => item.AccountId === accountId);

    return foundAccount?.Description || foundAccount?.AccountName || '';
  }, [
    groupId,
    accountId,
    areManagedAccountGroupsSelectable,
    accountGroups,
    unmanagedAccountsLabel,
    managedAccountGroups,
  ]);

  const updatedAt = useUpdatedAt();

  if (focusedAccountId === undefined) {
    return null;
  }

  if (!canShowComponent || !areOptionsLoaded) {
    return <Spinner />;
  }

  const accountDetails = get(accounts, `[${accountId}]`);

  const tabs = Object.entries(tabLabels)
    .filter(([tabKey]) => {
      switch (tabKey) {
        case 'performance':
          return showOrionPerformance;
        case 'activity':
          return showActivity;
        case 'income':
          return showIncomeTab && !isEmbeddedMobile;
        default:
          return true;
      }
    })
    .map(([tabKey, tabName]) => ({
      name: tabName,
      isActive: tabSlug === tabKey,
      action: () => {
        ADV.util.Router.go2('AccountPerformanceReporting', {
          clientId,
          groupId,
          accountId,
          tabSlug: tabKey,
          isApex,
        });
      },
    }));

  return (
    <PerformanceReportingProvider
      value={{
        updatedAt,
        selectedItemName,
        focusedAccountId,
      }}
    >
      <Header isEmbeddedMobile={isEmbeddedMobile}>
        <Box>
          <Text fontSize="h4" fontWeight={600} mb="4">
            {selectedItemName}
          </Text>
          <Box display="flex" alignItems="center" mb="8">
            <Text color="neutral.500" fontSize="small" mr="4">
              Last updated: {updatedAt}
            </Text>

            {!['rmdDashboard', 'riskProfile'].includes(tabSlug) && <RefreshButton />}
          </Box>
        </Box>

        <RightMenu>
          {periodComponent}

          {(tabSlug === 'activity' || tabSlug === 'allocation') &&
            canExportPersonalFinancesToCsv && <div id={EXPORT_CSV_BUTTON_CONTAINER_ID} />}

          <AccountMenu
            clientId={clientId}
            accountId={accountId}
            accountName={accountDetails?.AccountName || ''}
            managedAccountId={managedAccountId}
            currentClient={currentClient}
            groupId={groupId}
            onAccountSave={() => setShowAccountUpdateAlert(true)}
            accountDetails={accountDetails}
          />
        </RightMenu>
      </Header>

      {(accountId || groupId) && isEmbeddedMobile && <AccountHeader clientId={clientId} />}

      {tabSlug !== 'rmdDashboard' && (
        <TabsWrapper isEmbeddedMobile={isEmbeddedMobile}>
          <Tabs
            selectedIndex={tabs.findIndex(tab => tab.isActive)}
            onSelect={index => tabs[index].action()}
          >
            <TabList>
              {tabs.map(({ name }) => (
                <Tab isMobile={isEmbeddedMobile} key={name}>
                  {name}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </TabsWrapper>
      )}

      {showGrossOfFeeDisclaimer &&
        showOrionPerformance &&
        !isEmbeddedMobile &&
        ['summary', 'performance'].includes(tabSlug) && (
          <Alert
            variant="info"
            message="The information presented below is presented as Gross performance. Gross performance does not reflect the deduction of any fees charged to the client. Net performance is available and will be lower than the gross performance presented in the indicated sections below. Gross performance is displayed below at the option of your advisor. To view the net performance, please reach out to your advisor."
            mb="16"
          />
        )}

      {showAccountUpdateAlert && (
        <Alert
          variant="info"
          message="Account updated successfully. It may take a while for changes to be reflected on your account."
          onRequestClose={() => setShowAccountUpdateAlert(false)}
          mb="24"
        />
      )}

      {tabSlug === 'rmdDashboard' && <RmdDashboard />}

      {tabSlug === 'riskProfile' && <RiskProfile clientId={clientId} />}

      {tabSlug === 'summary' && <Summary />}

      {tabSlug === 'activity' && (
        <Transactions
          managedAccountId={managedAccountId}
          selectedItemName={selectedItemName}
          updatedAt={updatedAt}
        />
      )}

      {tabSlug === 'allocation' && (
        <Positions selectedItemName={selectedItemName} updatedAt={updatedAt} />
      )}

      {tabSlug === 'performance' && <Performance />}

      {tabSlug === 'income' && <Income />}
    </PerformanceReportingProvider>
  );
};
